<template>
  <v-container fluid>
    <v-card class="pa-6">
      <!-- Header -->
      <v-row align="center" class="mb-2">
        <v-col cols="12" md="8">
          <div class="text-h5 font-weight-bold">
            Incorporation &amp; Corporate Docket
          </div>
          <div class="caption grey--text">รายการ Docket ทั้งหมด</div>
        </v-col>
        <v-col cols="12" md="4" class="d-flex justify-end">
          <v-btn color="#0A7EA5" rounded dark @click="$router.push('/incorporate-docket-add')" v-if="authorize_add">
            <v-icon left>mdi-plus</v-icon>New Docket
          </v-btn>
        </v-col>
      </v-row>

      <!-- Search Row -->
      <v-row class="mt-2" align="center">
        <v-col cols="12" md="4">
          <v-text-field v-model="search" placeholder="Search (e.g. File No., Reg No., Document Category)" outlined dense
            hide-details clearable @keyup.enter="onSearchPagination"></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="search_status" :items="status_list" label="Status" item-text="descriptions"
            item-value="module_id" outlined dense hide-details clearable @change="onSearchPagination"></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="search_doc_category" :items="doc_category_list" label="Document Category | ชนิดเอกสาร"
            item-text="name" item-value="value" outlined dense hide-details clearable
            @change="onSearchPagination"></v-select>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn outlined @click="onSearchPagination"><v-icon left>mdi-magnify</v-icon>Search</v-btn>
        </v-col>
      </v-row>
      <div class="caption red--text mb-2">*input text and enter for search</div>

      <!-- Status Legend -->
      <div class="d-flex flex-wrap mb-3" style="gap:8px;">
        <v-chip small color="blue lighten-4" label><v-icon left x-small>mdi-circle</v-icon>In Progress</v-chip>
        <v-chip small color="green lighten-4" label><v-icon left x-small>mdi-circle</v-icon>Complete</v-chip>
        <v-chip small color="pink lighten-4" label><v-icon left x-small>mdi-circle</v-icon>Request Payment</v-chip>
        <v-chip small color="red lighten-4" label><v-icon left x-small>mdi-circle</v-icon>Cancelled</v-chip>
      </div>

      <!-- Table -->
      <v-card outlined tile>
        <div class="d-flex align-center px-2 pt-2">
          <v-select v-model="itemsPerPage" @change="onSearchPagination" style="width:150px" :items="item_per_page_list"
            label="Items per page" dense outlined hide-details></v-select>
        </div>
        <div class="text-center pt-2">
          <v-pagination class="ma-3" v-model="page" :length="pageCount" @input="loadDataByPage"
            total-visible="19"></v-pagination>
        </div>

        <v-data-table :headers="headers" :items="mDataArray2" :server-items-length="totalItems" hide-default-footer
          class="elevation-1" :loading="tableLoading" loading-text="Loading... Please wait">
          <template v-slot:progress>
            <div class="text-center pa-6">
              <v-progress-circular indeterminate color="primary" size="50" width="8"></v-progress-circular>
              <div class="mt-3 text-subtitle-1 primary--text">
                กำลังโหลดข้อมูล...
              </div>
            </div>
          </template>

          <!-- Inquiry Topic + doc_category -->
          <template v-slot:[`item.inquiry_topic`]="{ item }">
            <div>{{ item.inquiry_topic }}</div>
            <div class="d-flex flex-column mt-1" style="gap: 4px;" v-if="item.doc_category">
              <v-chip x-small label v-for="(cat, idx) in splitList(item.doc_category)" :key="'cat-' + idx"
                style="align-self: flex-start;">
                {{ cat }}
              </v-chip>
            </div>
          </template>

          <!-- Change Tracker -->
          <template v-slot:[`item.change_tracker`]="{ item }">
            <div class="d-flex flex-column" style="gap: 4px;" v-if="item.change_tracker">
              <v-chip x-small label color="pink lighten-4" v-for="(tr, idx) in splitList(item.change_tracker)"
                :key="'tr-' + idx" style="align-self: flex-start;">
                {{ tr }}
              </v-chip>
            </div>
          </template>

          <!-- Invoice (⭐ แสดง invoice_id ดิบไปก่อน — ยังไม่ join เลขที่ใบแจ้งหนี้จริงจาก inv_h) -->
          <template v-slot:[`item.inv_h_inv_id`]="{ item }">
            <div v-if="item.inv_h_inv_id">
              <div v-for="(inv, idx) in item.inv_h_inv_id.split(',')" :key="idx"
                style="font-family:monospace;color:#1d4ed8;font-weight:600;">
                {{ inv.trim() }}
              </div>
            </div>
            <span v-else style="font-family:monospace;color:#9ca3af;">-</span>
          </template>

          <!-- Status badge -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip small label :color="getDocketStatusColor(item.status)"
              :text-color="getDocketStatusTextColor(item.status)">
              <v-icon left x-small>mdi-circle</v-icon>{{ item.status }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn outlined x-small @click="$router.push(`/incorporate-docket-view/${item.id}`)">View</v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- Delete Dialog -->
      <v-dialog v-model="dialogDelete" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">Are you sure you want to delete this item?</v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="closeDelete">Cancel</v-btn>
            <v-btn color="blue darken-1" text @click="deleteItemConfirm">OK</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />
      <SuccessDialogPush :status="dialogAddPush" :text_color="text_color" :title="title" :message="message"
        :link="link" />
    </v-card>
  </v-container>
</template>

<script>
import SuccessDialog from "@/components/cards/SuccessDialog";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import { server } from "@/services/constants";
import api from "@/services/api";
import {
  getIncorporateDocketPagination,
  deleteIncorporateDocket,
} from "@/services/api-dtime/api_incorporate";

export default {
  name: "incorporate-docket-list",
  components: { SuccessDialog, SuccessDialogPush },
  data() {
    return {
      tableLoading: false,
      item_per_page_list: [10, 20, 100, 200, 500, 1000],
      page: 1,
      pageCount: 0,
      itemsPerPage: 100,
      totalItems: 0,
      mDataArray2: [],

      search: "",
      search_status: "",
      search_doc_category: "",

      status_list: [
        // { name: "In Progress", value: "In Progress" },
        // { name: "Complete", value: "Complete" },
        // { name: "Request Payment", value: "Request Payment" },
        // { name: "Cancelled", value: "Cancelled" },
      ],
      doc_category_list: [
        { name: "Company Incorporation Documents | เอกสารตั้งบริษัท", value: "Company Incorporation Documents | เอกสารตั้งบริษัท" },
        { name: "Certified Copy + Registration Items + Application", value: "Certified Copy + Registration Items + Application" },
        { name: "Memorandum of Association | บช.บริคณห์สนธิ", value: "Memorandum of Association | บช.บริคณห์สนธิ" },
        { name: "Articles of Association | ข้อบังคับ", value: "Articles of Association | ข้อบังคับ" },
        { name: "List of Shareholders (Form BorJor.5) | บัญชีรายชื่อผู้ถือหุ้น", value: "List of Shareholders (Form BorJor.5) | บัญชีรายชื่อผู้ถือหุ้น" },
        { name: "Financial Statement Submission Forms (SorBorChor.3)", value: "Financial Statement Submission Forms (SorBorChor.3)" },
        { name: "Tax Information | ข้อมูลภาษี", value: "Tax Information | ข้อมูลภาษี" },
        { name: "Share Certificates / Share Register | ใบหุ้น/ทะเบียนหุ้น", value: "Share Certificates / Share Register | ใบหุ้น/ทะเบียนหุ้น" },
        { name: "Minutes of Board of Directors' Meeting", value: "Minutes of Board of Directors' Meeting" },
        { name: "Minutes of Extraordinary Shareholders' Meeting", value: "Minutes of Extraordinary Shareholders' Meeting" },
        { name: "Minutes of Annual General Shareholders' Meeting", value: "Minutes of Annual General Shareholders' Meeting" },
        { name: "BOI Documents / Promotion Certificate", value: "BOI Documents / Promotion Certificate" },
        { name: "Various Licenses / Permits | ใบอนุญาตต่าง ๆ", value: "Various Licenses / Permits | ใบอนุญาตต่าง ๆ" },
        { name: "Agreements / Contracts | สัญญาต่าง ๆ", value: "Agreements / Contracts | สัญญาต่าง ๆ" },
        { name: "Legal Opinion | ความเห็นทางกฎหมาย", value: "Legal Opinion | ความเห็นทางกฎหมาย" },
        { name: "Work Rules | ข้อบังคับการทำงาน", value: "Work Rules | ข้อบังคับการทำงาน" },
        { name: "Due Diligence Report", value: "Due Diligence Report" },
        { name: "Others | อื่น ๆ", value: "Others | อื่น ๆ" },
      ],
      dialogDelete: false,
      dialogAdd: false,
      dialogAddPush: false,
      text_color: "text-h5 green--text text-center",
      title: "",
      message: "",
      link: "",
      id_del: 0,

      authorize_view: false,
      authorize_add: false,
      authorize_edit: false,
      authorize_del: false,
      authorize_id: "",
      userId: "",

      headers: [
        {
          text: "Client Name | ชื่อลูกค้า",
          value: "client_name",
          class: "bg-colorth tabletextwhite",
          divider: true,
          width: "180px",
        },
        {
          text: "Inquiry Topic | Document Category",
          value: "inquiry_topic",
          class: "bg-colorth tabletextwhite",
          divider: true,
        },
        {
          text: "Change Tracker | การเปลี่ยนแปลง",
          value: "change_tracker",
          class: "bg-colorth tabletextwhite",
          divider: true,
          width: "140px",
        },
        {
          text: "Date Received | วันที่รับเรื่อง",
          value: "date_received",
          class: "bg-colorth tabletextwhite",
          align: "center",
          divider: true,
          width: "130px",
        },
        {
          text: "Date Submitted | วันที่ส่งคำขอ",
          value: "date_submitted",
          class: "bg-colorth tabletextwhite",
          align: "center",
          divider: true,
          width: "130px",
        },
        {
          text: "Quotation",
          value: "doc_no",
          class: "bg-colorth tabletextwhite",
          align: "start",
          divider: true,
          width: "145px",
        },
        {
          text: "Invoice",
          value: "inv_h_inv_id",
          class: "bg-colorth tabletextwhite",
          align: "start",
          divider: true,
          width: "130px",
        },
        {
          text: "Status",
          value: "status",
          class: "bg-colorth tabletextwhite",
          align: "center",
          divider: true,
          width: "140px",
        },
        {
          text: "Action",
          value: "actions",
          class: "bg-colorth tabletextwhite",
          align: "center",
          sortable: false,
          divider: true,
          width: "80px",
        },
      ],
    };
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    this.$hideLoader();
    this.userId = localStorage.getItem(server.USER_ID);
    this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

    if (!this.authorize_id || this.authorize_id == 0) {
      this.setupAlertDialog(
        true,
        "Authorize Failed",
        "Please Logout And Login Again",
        "text-h5 red--text text-center",
      );
      this.$router.back();
      return;
    }

    let parser = document.createElement("a");
    parser.href = window.location;
    const router_path = parser.pathname.replace("/", "");
    const { default: api } = await import("@/services/api");
    const res_auth = await api.getAuthorize(this.userId, router_path);
    this.authorize_view = res_auth.data[0].smd_view >= 1;
    this.authorize_add = res_auth.data[0].smd_add >= 1;
    this.authorize_edit = res_auth.data[0].smd_edit >= 1;
    this.authorize_del = res_auth.data[0].smd_del >= 1;

    if (!this.authorize_view) {
      this.$router.back();
      return;
    }

    await this.loaddataLitigationCourtStatus();
    await this.onSearchPagination();
  },
  methods: {
    async loaddataLitigationCourtStatus() {
      const result = await api.getClientTimelineStatusCongFigByModule("Corporate");
      this.status_list = (result.data || []).map(item => ({
        ...item,
        module_id: item.module_id
      }));
      console.log(this.status_list)

    },
    getDocketStatusColor(status) {
      const map = {
        "In Progress": "blue lighten-4",
        Complete: "green lighten-4",
        "Request Payment": "pink lighten-4",
        Cancelled: "red lighten-4",
      };
      return map[status] || "grey lighten-3";
    },
    splitList(str) {
      if (!str) return [];
      return str
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    getDocketStatusTextColor(status) {
      const map = {
        "In Progress": "blue darken-2",
        Complete: "green darken-2",
        "Request Payment": "pink darken-2",
        Cancelled: "red darken-2",
      };
      return map[status] || "grey darken-1";
    },

    async loadDataByPage(pageNumber) {
      this.tableLoading = true;
      try {
        const company_id = localStorage.getItem(server.COMPANYID);
        const res = await getIncorporateDocketPagination(
          pageNumber,
          this.itemsPerPage,
          "id",
          "DESC",
          company_id,
          this.search,
          this.search_status,
          this.search_doc_category,
        );
        this.mDataArray2 = res.data.data;
        this.totalItems = res.data.pagination.totalRecords;
        this.page = res.data.pagination.currentPage;
        this.pageCount = res.data.pagination.totalPages;

      } catch (error) {
        console.error("Error loading dockets:", error);
      } finally {
        this.tableLoading = false;
      }
    },

    async onSearchPagination() {
      this.page = 1;
      this.tableLoading = true;
      try {
        const company_id = localStorage.getItem(server.COMPANYID);
        const res = await getIncorporateDocketPagination(
          1,
          this.itemsPerPage,
          "id",
          "DESC",
          company_id,
          this.search,
          this.search_status,
          this.search_doc_category,
        );
        this.mDataArray2 = res.data.data;
        this.totalItems = res.data.pagination.totalRecords;
        this.pageCount = res.data.pagination.totalPages;
        console.log(this.mDataArray2)
      } catch (error) {
        console.error("Error searching dockets:", error);
        this.mDataArray2 = [];
        this.totalItems = 0;
        this.pageCount = 0;
      } finally {
        this.tableLoading = false;
      }
    },

    deleteItem(id) {
      this.id_del = id;
      this.dialogDelete = true;
    },

    async deleteItemConfirm() {
      try {
        await deleteIncorporateDocket(this.id_del);
        this.setupAlertDialog(
          true,
          "Success",
          "ลบข้อมูลสำเร็จ",
          "text-h5 green--text text-center",
        );
        await this.onSearchPagination();
      } catch (error) {
        console.error("Error deleting docket:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "ไม่สามารถลบข้อมูลได้",
          "text-h5 red--text text-center",
        );
      }
      this.closeDelete();
    },

    closeDelete() {
      this.dialogDelete = false;
      this.id_del = 0;
    },

    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
    },
  },
};
</script>

<style scoped>
.bg-colorth {
  background-color: #8d8d8d !important;
}

.tabletextwhite {
  color: white !important;
}
</style>
